/* eslint-disable no-console */
import type { RumEvent } from '@datadog/browser-rum';
import { datadogRum } from '@datadog/browser-rum';

import {
  APP_IS_DEVELOPMENT,
  DATADOG_RUM_APPLICATION_ID,
  DATADOG_RUM_CLIENT_TOKEN,
  DATADOG_RUM_DEFAULT_PRIVACY_LEVEL,
  DATADOG_RUM_ENABLED,
  DATADOG_RUM_ENV,
  DATADOG_RUM_MANUAL_REPLAY_CONTROL,
  DATADOG_RUM_SERVICE,
  DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE,
  DATADOG_RUM_SESSION_SAMPLE_RATE,
  DATADOG_RUM_SITE,
  DATADOG_RUM_TRACK_USER_INTERACTIONS,
  DATADOG_RUM_VERSION,
} from '../../configs/datadog';
import { logError } from '../logging';

import { getRumTrackingConsent } from './datadogRum';
import { normalizeRumPath } from './useRumViewTracking';

const UUID_PATH_SEGMENT_PATTERN = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;

function sanitizeRumPathSegment(segment: string): string {
  try {
    return UUID_PATH_SEGMENT_PATTERN.test(decodeURIComponent(segment)) ? ':uuid' : segment;
  } catch {
    return segment;
  }
}

// RUM URL fields are sent verbatim (defaultPrivacyLevel only masks DOM text, not
// URLs), so strip identifiers before they leave the browser: drop query/hash,
// normalize known frontend routes, and replace UUID path segments everywhere else.
function sanitizeParsedRumUrl(parsedUrl: URL): void {
  /* eslint-disable no-param-reassign */
  parsedUrl.search = '';
  parsedUrl.hash = '';
  parsedUrl.pathname = normalizeRumPath(parsedUrl.pathname)
    .split('/')
    .map(sanitizeRumPathSegment)
    .join('/');
  /* eslint-enable no-param-reassign */
}

function sanitizeRumUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);
    sanitizeParsedRumUrl(parsedUrl);
    return parsedUrl.toString();
  } catch {
    return url;
  }
}

// Placeholder base for parsing root-/protocol-relative references; the output
// stays relative, so this origin never appears in the sanitized text.
const RELATIVE_URL_BASE = 'https://relative.invalid';

function sanitizeRelativeRumUrl(ref: string): string {
  try {
    const parsedUrl = new URL(ref, RELATIVE_URL_BASE);
    sanitizeParsedRumUrl(parsedUrl);
    return ref.startsWith('//') ? `//${parsedUrl.host}${parsedUrl.pathname}` : parsedUrl.pathname;
  } catch {
    return ref;
  }
}

// Error messages/stacks are free text (often backend error strings) and are not
// covered by defaultPrivacyLevel, so redact anything identifying before send.
const ABSOLUTE_URL_IN_TEXT_PATTERN = /https?:\/\/[^\s"'<>()]+/gi;
// Root-relative (/api/x) and protocol-relative (//host/x) references, only at a
// token boundary so text like "and/or" or an already-sanitized absolute URL's
// path is not re-matched. Captures the boundary to avoid regex lookbehind.
const RELATIVE_URL_IN_TEXT_PATTERN = /(^|[\s"'(<=,])(\/{1,2}[^\s"'<>()/][^\s"'<>()]*)/g;
// Stack frames end in :line:col; when the URL has a query or hash, URL parsing
// would treat that suffix as part of it and drop it, so split it off first.
const SOURCE_LOCATION_SUFFIX_PATTERN = /(?::\d+){1,2}$/;
const EMAIL_IN_TEXT_PATTERN = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;
const UUID_IN_TEXT_PATTERN = /[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}/gi;

function preservingSourceLocation(ref: string, sanitize: (url: string) => string): string {
  if (!/[?#]/.test(ref)) {
    return sanitize(ref);
  }
  const suffix = SOURCE_LOCATION_SUFFIX_PATTERN.exec(ref)?.[0] ?? '';
  return sanitize(ref.slice(0, ref.length - suffix.length)) + suffix;
}

function scrubRumText(text: string): string {
  return text
    .replace(ABSOLUTE_URL_IN_TEXT_PATTERN, (url) => preservingSourceLocation(url, sanitizeRumUrl))
    .replace(
      RELATIVE_URL_IN_TEXT_PATTERN,
      (_match, boundary: string, ref: string) =>
        boundary + preservingSourceLocation(ref, sanitizeRelativeRumUrl),
    )
    .replace(EMAIL_IN_TEXT_PATTERN, '[email]')
    .replace(UUID_IN_TEXT_PATTERN, ':uuid');
}

export function initDatadogRum(): void {
  if (!DATADOG_RUM_ENABLED || !DATADOG_RUM_APPLICATION_ID || !DATADOG_RUM_CLIENT_TOKEN) {
    if (APP_IS_DEVELOPMENT) {
      console.log('🐕 Datadog RUM: Disabled', {
        enabled: DATADOG_RUM_ENABLED,
        hasApplicationId: Boolean(DATADOG_RUM_APPLICATION_ID),
        hasClientToken: Boolean(DATADOG_RUM_CLIENT_TOKEN),
      });
    }
    return;
  }

  try {
    if (APP_IS_DEVELOPMENT) {
      console.log('🐕 Datadog RUM: Initializing...', {
        env: DATADOG_RUM_ENV,
        sessionSampleRate: DATADOG_RUM_SESSION_SAMPLE_RATE,
        sessionReplaySampleRate: DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE,
        trackUserInteractions: DATADOG_RUM_TRACK_USER_INTERACTIONS,
        manualReplayControl: DATADOG_RUM_MANUAL_REPLAY_CONTROL,
      });
    }

    datadogRum.init({
      applicationId: DATADOG_RUM_APPLICATION_ID,
      clientToken: DATADOG_RUM_CLIENT_TOKEN,
      site: DATADOG_RUM_SITE,
      service: DATADOG_RUM_SERVICE,
      env: DATADOG_RUM_ENV,
      version: DATADOG_RUM_VERSION,
      sessionSampleRate: DATADOG_RUM_SESSION_SAMPLE_RATE,
      sessionReplaySampleRate: DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE,
      trackingConsent: getRumTrackingConsent(),
      // Views are named manually (via useRumViewTracking) so overlay routes get
      // correct names instead of RUM's URL-based auto-views.
      trackViewsManually: true,
      trackResources: true,
      trackUserInteractions: DATADOG_RUM_TRACK_USER_INTERACTIONS,
      trackLongTasks: true,
      startSessionReplayRecordingManually: DATADOG_RUM_MANUAL_REPLAY_CONTROL,
      // Mask action names so click heatmaps never leak PII from member rows;
      // elements with an explicit data-dd-action-name override with safe strings.
      enablePrivacyForActionName: true,
      defaultPrivacyLevel: DATADOG_RUM_DEFAULT_PRIVACY_LEVEL,
      beforeSend: (event: RumEvent) => {
        // Sanitize every URL field so identifiers never reach Datadog. Datadog's
        // beforeSend requires mutating the event in place (it returns a boolean).
        /* eslint-disable no-param-reassign */
        event.view.url = sanitizeRumUrl(event.view.url);
        if (event.view.referrer) {
          event.view.referrer = sanitizeRumUrl(event.view.referrer);
        }
        if (event.type === 'resource') {
          event.resource.url = sanitizeRumUrl(event.resource.url);
        }
        if (event.type === 'error') {
          if (event.error.resource) {
            event.error.resource.url = sanitizeRumUrl(event.error.resource.url);
          }
          if (event.error.message) {
            event.error.message = scrubRumText(event.error.message);
          }
          if (event.error.stack) {
            event.error.stack = scrubRumText(event.error.stack);
          }
          event.error.causes?.forEach((cause) => {
            if (cause.message) {
              cause.message = scrubRumText(cause.message);
            }
            if (cause.stack) {
              cause.stack = scrubRumText(cause.stack);
            }
          });
        }
        /* eslint-enable no-param-reassign */

        return true;
      },
    });

    if (APP_IS_DEVELOPMENT) {
      console.log('🐕 Datadog RUM: Successfully initialized');
    }
  } catch (e) {
    const eIsError = e instanceof Error;
    const error = eIsError ? e : new Error(String(e));
    if (!eIsError) {
      error.cause = e;
    }
    logError(error, {
      context: 'datadog_rum_init',
      isHandled: true,
      severity: 'error',
    });

    if (APP_IS_DEVELOPMENT) {
      console.error('🐕 Datadog RUM: Initialization failed:', error);
    }
  }
}
